import type { Tier } from "@/features/funnel/types";
import { screenFor } from "./open-case";

/** The mission window (the live mission counts down the same 24 hours). */
export const WINDOW_MS = 24 * 60 * 60 * 1000;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hms = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
};

/** The 24h window on the display of the laptop in the case (taps go
 *  through to the laptop): digits over 24
 *  hour-segments going out, the hour under way blinking. In the case
 *  picture's box (cqw), in the case's light (--sm-ring). */
export function LidClock({ left, tier }: { left: number; tier: Tier }) {
  const litHours = Math.ceil(Math.min(1, Math.max(0, left / WINDOW_MS)) * 24);
  return (
    <>
      <div
        style={screenFor(tier)}
        className="pointer-events-none absolute flex animate-[smScreenOn_.8s_ease-out_.2s_both] flex-col items-center justify-center overflow-hidden rounded-[.8cqw] border-[.7cqw] border-[#0b0c0e] bg-[radial-gradient(ellipse_at_50%_40%,color-mix(in_srgb,var(--sm-ring)_14%,#05070a),#020304_85%)] shadow-[0_0_0_.25cqw_#3a3c42,0_.6cqw_1.2cqw_rgba(0,0,0,.8),inset_0_0_2.5cqw_rgba(0,0,0,.95),0_0_3cqw_color-mix(in_srgb,var(--sm-ring)_22%,transparent)]"
      >
        <div className="font-mono text-[1.9cqw] tracking-[.3em] text-[color-mix(in_srgb,var(--sm-ring)_55%,#8c8070)] uppercase">
          {left > 0 ? "Time remaining" : "Window closed"}
        </div>
        <div className="mt-[.6cqw] font-mono text-[7.4cqw] leading-none font-semibold tracking-[.02em] text-[var(--sm-ring)] tabular-nums [text-shadow:0_0_3cqw_color-mix(in_srgb,var(--sm-ring)_60%,transparent)]">
          {hms(left)}
        </div>
        {/* 24 hours, going out; the hour under way blinking */}
        <div className="mt-[1.3cqw] flex w-[82%] gap-[.4cqw]">
          {HOURS.map((h) => (
            <span
              key={h}
              className={`h-[1cqw] flex-1 rounded-[1px] ${
                h < litHours
                  ? `bg-[var(--sm-ring)] shadow-[0_0_1cqw_var(--sm-ring)] ${
                      h === litHours - 1
                        ? "animate-[smBlink_1s_steps(2,jump-none)_infinite]"
                        : ""
                    }`
                  : "bg-white/[.08]"
              }`}
            />
          ))}
        </div>
        {/* the glass */}
        <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,.18)_0_1px,transparent_1px_3px)]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-[linear-gradient(170deg,rgba(255,255,255,.06),transparent_60%)]" />
      </div>
      <style>{`
        @keyframes smBlink{0%{opacity:1}50%{opacity:.15}100%{opacity:1}}
        @keyframes smScreenOn{0%{transform:scaleY(.02);filter:brightness(3)}30%{transform:scaleY(1);filter:brightness(1.8)}45%{opacity:.6}55%{opacity:1;filter:none}70%{opacity:.85}100%{opacity:1;transform:none;filter:none}}
      `}</style>
    </>
  );
}

/** The same window as one line across the top of the laptop's screen, once
 *  they are in it (reviewing the mission): it comes up from the middle. */
export function TopClock({ left }: { left: number }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-[8] flex animate-[smClockUp_.7s_cubic-bezier(.2,.8,.3,1)_both] items-center justify-center gap-3 border-b border-[color-mix(in_srgb,var(--sm-ring)_35%,transparent)] bg-black/85 py-2.5 font-mono backdrop-blur">
      <span className="text-[10.5px] tracking-[.3em] text-[color-mix(in_srgb,var(--sm-ring)_55%,#8c8070)] uppercase">
        {left > 0 ? "Time remaining" : "Window closed"}
      </span>
      <span className="text-[19px] font-semibold text-[var(--sm-ring)] tabular-nums [text-shadow:0_0_10px_color-mix(in_srgb,var(--sm-ring)_60%,transparent)]">
        {hms(left)}
      </span>
      <style>{`@keyframes smClockUp{from{translate:0 42dvh;scale:1.9}to{translate:0 0;scale:1}}`}</style>
    </div>
  );
}
